// src/pages/dashboard/branch/BranchDashboard.tsx
import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	FiArrowLeft,
	FiArrowRight, FiBarChart2,
	FiCoffee,
	FiEye,
	FiLayers,
	FiSettings,
	FiUsers
} from 'react-icons/fi';
import { Link, useParams } from 'react-router-dom';
import Card from '../../../components/ui/Card';
import { useAppSelector } from '../../../store/hooks';
import { selectCurrentBranch } from '../../../store/selectors';
import { BranchCounts } from '../../../types/custom';
import { supabase } from '../../../utils/supabaseClient';
import { castJsonField } from '../../../types/custom';
import { RiRobot2Line } from 'react-icons/ri';
import { getBranchName } from '../../../utils/brandHelper';

const BranchDashboard: React.FC = () => {
	const { t, i18n } = useTranslation();
	const { branchId } = useParams<{ branchId: string }>();
	// const [branch, setBranch] = useState<Branch | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [branchCounts, setBranchCounts] = useState<BranchCounts | null>(null);
	const [branchKey, setBranchKey] = useState<string | null>(null);
	// const [isPublishing, setIsPublishing] = useState(false);
	// const [publishMessage, setPublishMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

	// const dispatch = useAppDispatch()
	// const organization = useAppSelector(selectCurrentOrganization);
	const branch = useAppSelector(selectCurrentBranch)
	const isRTL = i18n.language === 'ar'

	useEffect(() => {
		const fetchBranchCounts = async () => {
			if (!branchId) return;

			setIsLoading(true);
			try {
				const { data, error } = await supabase.rpc(
					'get_branch_counts',
					{
						p_branch_id: branchId
					}
				);
				if (error) throw error

				const parsed = castJsonField<BranchCounts>(data as any);
				setBranchCounts(parsed ?? null)
			} catch (error) {
				console.error('Error fetching branch counts:', error);
			} finally {
				setIsLoading(false);
			}
		};

		fetchBranchCounts();
	}, [branchId]);

	useEffect(() => {
		if (branch && branch.branch_key) {
			setBranchKey(branch.branch_key);
			console.log("Branch key set to:", branch.branch_key);
		}
	}, [branch]);

	const quickLinks = [
		{
			title: t('dashboard.manageCategories'),
			description: t('dashboard.manageCategoriesDesc'),
			icon: <FiLayers className="h-6 w-6 text-primary" />,
			path: `categories`
		},
		{
			title: t('dashboard.manageMenu'),
			description: t('dashboard.manageMenuDesc'),
			icon: <FiCoffee className="h-6 w-6 text-primary" />,
			path: `menu`
		},
		{
			title: t('dashboard.agentSettings'),
			description: t('dashboard.agentSettingsDesc'),
			icon: <RiRobot2Line className="h-6 w-6 text-primary" />,
			path: `agent`
		},
		{
			title: t('dashboard.settings'),
			description: t('dashboard.settingsDesc'),
			icon: <FiSettings className="h-6 w-6 text-primary" />,
			path: `settings`
		},
		{
			title: t('dashboard.viewMenu'),
			description: t('dashboard.viewMenuDesc'),
			icon: <FiEye className="h-6 w-6 text-primary" />,
			path: `https://dineai.nawaah.org/${branchKey}/menu`,
			newTab: true,
		},
		{
			title: t('dashboard.viewAnalytics'),
			description: t('dashboard.viewAnalyticsDesc'),
			icon: <FiBarChart2 className="h-6 w-6 text-primary" />,
			path: `analytics`
		}
	];

	if (isLoading) {
		return (
			<div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
				<div className="animate-pulse">
					<div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-4"></div>
					<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-8"></div>

					<div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
						{[1, 2, 3, 4].map(i => (
							<div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
								<div className="h-6 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-2"></div>
								<div className="h-12 bg-gray-200 dark:bg-gray-700 rounded w-2/3 mb-4"></div>
								<div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/4"></div>
							</div>
						))}
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
			<motion.div
				initial={{ opacity: 0, y: -10 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5 }}
				className="mb-8"
			>
				<div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
					<div>
						<h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
							{getBranchName(branch!, i18n.language) ? t('dashboard.welcomeTo', { name: getBranchName(branch!, i18n.language) }) : t('dashboard.dashboard')}
						</h1>
						<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
							{t('dashboard.overview')}
						</p>
					</div>

					{/* <div className="relative flex flex-col">
						<Button
							onClick={handlePublishMenu}
							isLoading={isPublishing}
							leftIcon={<FiGlobe />}
							className="bg-green-600 hover:bg-green-700 text-white"
						>
							{t('dashboard.publishChanges')}
						</Button>

						{publishMessage && (
							<motion.div
								initial={{ opacity: 0, y: -10 }}
								animate={{ opacity: 1, y: 0 }}
								exit={{ opacity: 0, y: -10 }}
								className={`absolute top-full right-0 translate-y-4 w-lg max-w-full px-3 py-2 rounded-md text-sm ${publishMessage.type === 'success'
									? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
									: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800'
									}`}
							>
								{publishMessage.text}
							</motion.div>
						)}
					</div> */}
				</div>
			</motion.div>

			{/* Summary Stats */}
			<motion.div
				initial={{ opacity: 0, y: 10 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5, delay: 0.1 }}
				className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8"
			>
				<Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-none">
					<div className="flex items-center">
						<div className="p-3 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 me-4">
							<FiCoffee className="h-6 w-6" />
						</div>
						<div>
							<p className="text-sm font-medium text-blue-600 dark:text-blue-400">
								{t('dashboard.totalItems')}
							</p>
							<p className="text-2xl font-semibold text-gray-900 dark:text-white">{branchCounts?.items_count}</p>
						</div>
					</div>
				</Card>

				<Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-none">
					<div className="flex items-center">
						<div className="p-3 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 me-4">
							<FiLayers className="h-6 w-6" />
						</div>
						<div>
							<p className="text-sm font-medium text-green-600 dark:text-green-400">
								{t('dashboard.mainCategories')}
							</p>
							<p className="text-2xl font-semibold text-gray-900 dark:text-white">{branchCounts?.main_categories_count}</p>
						</div>
					</div>
				</Card>

				<Card className="bg-gradient-to-br from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20 border-none">
					<div className="flex items-center">
						<div className="p-3 rounded-full bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 me-4">
							<FiLayers className="h-6 w-6" />
						</div>
						<div>
							<p className="text-sm font-medium text-orange-600 dark:text-orange-400">
								{t('dashboard.subCategories')}
							</p>
							<p className="text-2xl font-semibold text-gray-900 dark:text-white">{branchCounts?.sub_categories_count}</p>
						</div>
					</div>
				</Card>


			</motion.div>

			{/* Quick Links */}
			<motion.div
				initial={{ opacity: 0, y: 10 }}
				animate={{ opacity: 1, y: 0 }}
				transition={{ duration: 0.5, delay: 0.2 }}
			>
				<h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
					{t('dashboard.quickActions')}
				</h2>
				<div className="grid grid-cols-1 md:grid-cols-3 gap-6">
					{quickLinks.map((link, index) => (
						<Link
							key={index}
							to={link.path}
							className="block"
							target={link.newTab ? '_blank' : ''}
						>
							<Card className="h-full hover:shadow-md transition-shadow duration-300 border border-gray-200 dark:border-gray-700">
								<div className="flex items-start flex-wrap">
									<div className="flex-shrink-0">
										{link.icon}
									</div>
									<div className="ms-4 flex-1">
										<h3 className="text-base font-medium text-gray-900 dark:text-white">
											{link.title}
										</h3>
										<p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
											{link.description}
										</p>
									</div>
									<div className="ms-2">
										{
											isRTL ?
												<FiArrowLeft className="h-5 w-5 text-gray-400" />
												:
												<FiArrowRight className="h-5 w-5 text-gray-400" />
										}
									</div>
								</div>
							</Card>
						</Link>
					))}
				</div>
			</motion.div>
		</div>
	);
};

export default BranchDashboard;